import { BaseElement } from 'base';
import { bus } from 'bus';
import { PRODUCT_DESCRIPTION_REQUEST_TOPIC } from 'product-description-topic';

const FIT_TRIGGER = '[data-fit-description-trigger]';

class OptionPicker extends BaseElement {
  #action = null;
  #committed = null;
  #epoch = 0;
  #pending = null;
  #settled = null;

  onConnect() {
    this.#pending = null;
    this.removeAttribute('aria-busy');
    this.#committed = this.#inputs.find(input => input.checked) ?? null;
    this.#settled = this.#committed;
    if (this.#committed) this.#renderHead(this.#committed);

    this.listen(this, 'change', this.#onChange);
    this.listen(this, 'keydown', this.#onKeydown);

    const fit = this.q(FIT_TRIGGER);
    if (fit) this.listen(fit, 'click', this.#onFit);
  }

  run(action) {
    this.#action = action;
    return this;
  }

  get value() {
    if (this.#committed) return this.#committed.value;
    return (
      this.q(
        '[data-picker-value-group]:not([hidden]) [data-picker-value]'
      )?.textContent.trim() ?? ''
    );
  }

  get index() {
    return Number(this.dataset.optionIndex);
  }

  restore(value) {
    const input = this.#inputs.find(candidate => candidate.value === value);
    if (!input) return;
    this.#settled = input;
    if (input !== this.#committed) this.#rollback(input);
  }

  sync({ states = {} } = {}) {
    const notes = this.#byItem('[data-sold-out-note]');
    const swatches = this.#byItem('.color-swatch');

    for (const input of this.#inputs) {
      const state = states[input.value];
      if (!state) continue;

      const item = input.parentElement;
      const available = state === 'available';
      item.toggleAttribute('data-sold-out', state === 'sold-out');
      item.toggleAttribute('data-missing', state === 'missing');
      const note = notes.get(item);
      if (note) note.hidden = available;
      swatches
        .get(item)
        ?.classList.toggle('color-swatch--unavailable', !available);
    }

    this.#renderSuffix(states[this.value] === 'sold-out');
  }

  get #inputs() {
    return this.qa('.option-picker__item > input[type="radio"]');
  }

  #byItem(selector) {
    return new Map(
      this.qa(`.option-picker__item ${selector}`).map(control => [
        control.closest('.option-picker__item'),
        control
      ])
    );
  }

  #headScope(group) {
    return group ? `[data-picker-value-group][data-group="${group}"] ` : '';
  }

  #renderHead(input) {
    const group = input.parentElement.dataset.group;
    if (group) {
      const valueGroups = this.qa('[data-picker-value-group][data-group]');
      for (const valueGroup of valueGroups) {
        valueGroup.hidden = valueGroup.dataset.group !== group;
      }
    }

    const value = this.q(`${this.#headScope(group)}[data-picker-value]`);
    if (value) value.textContent = input.value;
    this.#renderSuffix();
  }

  #renderSuffix(soldOut = false) {
    const item = this.#committed?.parentElement;
    const suffix = this.q(
      `${this.#headScope(item?.dataset.group)}[data-picker-suffix]`
    );
    if (!suffix) return;
    suffix.hidden = item ? !item.hasAttribute('data-sold-out') : !soldOut;
  }

  #commit(input) {
    this.#committed = input;
    this.#epoch += 1;
  }

  #onChange(event) {
    const input = event.target;
    if (!input.checked || !this.#inputs.includes(input)) return;
    if (input === this.#committed) return;

    const previous = this.#committed;
    this.#commit(input);
    const epoch = this.#epoch;
    this.#renderHead(input);
    this.#runAction(input, previous, epoch);
  }

  #onKeydown(event) {
    if (event.key !== 'Enter' || event.repeat) return;
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey)
      return;

    const input = event.target;
    if (!this.#inputs.includes(input)) return;

    event.preventDefault();
    if (input.checked) return;

    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #onFit(event) {
    const trigger = event.currentTarget;
    const target = trigger.dataset.fitDescriptionTarget;
    if (!target) return;

    const keyboard = event.detail === 0;
    if (trigger !== document.activeElement)
      trigger.focus({ preventScroll: true, focusVisible: keyboard });
    bus.publish(PRODUCT_DESCRIPTION_REQUEST_TOPIC, {
      target,
      section: 'description',
      keyboard
    });
  }

  async #runAction(input, previous, epoch) {
    if (!this.#action) {
      this.#settled = input;
      return;
    }

    const change = {
      index: this.index,
      value: input.value,
      previous: previous?.value ?? ''
    };

    let pending = null;
    try {
      pending = this.#action(change);
      if (!pending || typeof pending.then !== 'function') {
        this.#settled = input;
        return;
      }

      this.#pending = pending;
      this.setAttribute('aria-busy', 'true');
      await pending;
      if (this.#epoch === epoch) this.#settled = input;
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('[option-picker] action failed:', error);
      if (!this.isConnected || this.#epoch !== epoch) return;
      this.#rollback(this.#settled);
    } finally {
      if (this.isConnected && this.#pending === pending) {
        this.#pending = null;
        this.removeAttribute('aria-busy');
      }
    }
  }

  #rollback(input) {
    if (!input) return;

    const hadFocus = this.contains(this.getRootNode().activeElement);
    this.#select(input);
    if (hadFocus) input.focus();
  }

  #select(input) {
    input.checked = true;
    this.#commit(input);
    this.#renderHead(input);
  }
}

if (!customElements.get('option-picker'))
  customElements.define('option-picker', OptionPicker);
